import React from "react";

/**
 * Inside Connexis — Device frames for consumer-brand showcase.
 *
 * Two pure-CSS/SVG frames so we can present consumer brand screenshots
 * as if they're being viewed on a real laptop or phone:
 *
 *  - <LaptopFrame> wraps a wide 16:10 screenshot in a MacBook-style bezel
 *    with a notch, stand & subtle shadow.
 *  - <PhoneFrame> wraps a 9:19 screenshot in an iPhone-style bezel with
 *    Dynamic Island.
 *
 * Both are visual chrome only — no clicks, no links. Used for the
 * "First-party consumer brands" section to make the screen-grabs feel
 * authentic & premium.
 */

export function LaptopFrame({ src, alt, testid }) {
  return (
    <div className="relative w-full" data-testid={testid}>
      {/* Lid / bezel — slightly thicker top to feel premium */}
      <div className="relative rounded-t-2xl bg-gradient-to-b from-slate-900 to-slate-800 p-2.5 pb-2 shadow-[0_22px_55px_-12px_rgba(15,23,42,0.45),0_8px_22px_-4px_rgba(15,23,42,0.22)]">
        {/* Camera notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-14 h-1.5 rounded-full bg-slate-700/90 z-10 flex items-center justify-center">
          <span className="size-0.5 rounded-full bg-slate-500" />
        </div>
        {/* Screen */}
        <div className="relative rounded-md overflow-hidden bg-slate-100 aspect-[16/10] ring-1 ring-white/5">
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="w-full h-full object-cover object-top"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.classList.add("bg-gradient-to-br", "from-slate-100", "to-slate-200");
            }}
          />
          {/* Subtle glass reflection */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/[0.08] via-transparent to-transparent" />
        </div>
      </div>
      {/* Bottom hinge + base */}
      <div className="relative">
        <div className="h-2.5 bg-gradient-to-b from-slate-700 to-slate-800 rounded-b-md mx-auto shadow-md" style={{ width: "100%" }} />
        {/* Hinge dimple */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-slate-900/60 rounded-b-md" />
        {/* Stand shadow under the laptop */}
        <div className="h-1 bg-gradient-to-b from-slate-300/40 to-transparent mx-auto blur-sm" style={{ width: "70%" }} />
      </div>
    </div>
  );
}

export function PhoneFrame({ src, alt, testid }) {
  return (
    <div className="relative mx-auto w-full max-w-[220px] aspect-[9/19] rounded-[34px] bg-slate-900 p-[8px] shadow-[0_24px_50px_-12px_rgba(15,23,42,0.35),0_6px_18px_-4px_rgba(15,23,42,0.18)]" data-testid={testid}>
      <div className="absolute inset-0 rounded-[34px] ring-1 ring-white/10 pointer-events-none" />
      <div className="relative h-full w-full rounded-[26px] bg-white overflow-hidden">
        {/* Dynamic Island */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[60px] h-[18px] rounded-full bg-slate-900 z-20" />
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover object-top"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentElement.classList.add("bg-gradient-to-br", "from-slate-100", "to-slate-200");
          }}
        />
      </div>
    </div>
  );
}
